import { Box, Button, Checkbox, Flex, Icon, Input, InputGroup, InputLeftElement, Spinner, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiSearch } from "react-icons/fi";
import type { SelectorValue } from "../../input/Selector/SelectorValue";

interface Props {
  label: string;
  options: SelectorValue[];
  chosen: string[];
  onChange: (chosen: string[]) => void;
  isLoading?: boolean;
  failedToLoad?: boolean;
  onRetry?: () => void;
  emptyMessage: string;
}

export const CheckboxListFilter = ({
  label,
  options,
  chosen,
  onChange,
  isLoading = false,
  failedToLoad = false,
  onRetry,
  emptyMessage,
}: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const needle = search.trim().toLowerCase();
  const shown = options.filter((option) => option.label.toLowerCase().includes(needle));
  const toggle = (value: string, checked: boolean) =>
    onChange(checked ? [...chosen, value] : chosen.filter((v) => v !== value));

  return (
    <Stack spacing="2">
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="smMuted" />
        </InputLeftElement>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("Search in {{filter}}", { filter: label.toLowerCase() }) as string}
          aria-label={t("Search in {{filter}}", { filter: label.toLowerCase() }) as string}
          rounded="md"
          autoFocus
        />
      </InputGroup>
      <Box maxH="64" overflowY="auto" role="group" aria-label={label}>
        {isLoading ? (
          <Spinner size="sm" />
        ) : failedToLoad ? (
          <Flex align="center" gap="2" wrap="wrap">
            <Text fontSize="sm" color="smError">
              {t("Could not load the filter choices. Try again.")}
            </Text>
            {onRetry && (
              <Button size="sm" variant="link" colorScheme="teal" onClick={onRetry}>
                {t("Try again")}
              </Button>
            )}
          </Flex>
        ) : options.length === 0 ? (
          <Text fontSize="sm" color="smMuted">
            {emptyMessage}
          </Text>
        ) : shown.length === 0 ? (
          <Text fontSize="sm" color="smMuted">
            {t("No values match the search")}
          </Text>
        ) : (
          <Stack spacing="1">
            {shown.map((option) => (
              <Checkbox
                key={option.value}
                size="sm"
                isChecked={chosen.includes(option.value)}
                onChange={(e) => toggle(option.value, e.target.checked)}
              >
                {option.label}
              </Checkbox>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
